import type { CSSProperties, ReactElement, ReactNode } from 'react';

import styles from './SafeArea.module.css';

/**
 * Замена `SafeArea` из Flutter и `lib/src/utils/custom_safe_area.dart`.
 *
 * Во Flutter это Padding на величину `MediaQuery.padding`; в вебе те же
 * вырезы даёт `env(safe-area-inset-*)`. `CustomSafeArea` дополнительно
 * вычитал высоту AppBar — здесь нет ни Scaffold, ни AppBar, поэтому эта
 * часть не переносится, как и `maintainBottomViewPadding`: клавиатура
 * учитывается метриками окна (`installScreenMetrics`).
 */
export interface SafeAreaInsets {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

export interface SafeAreaProps {
  top?: boolean;
  bottom?: boolean;
  left?: boolean;
  right?: boolean;
  /** `minimum` из Flutter: CSS-длины, например `wv(40)`. */
  minimum?: SafeAreaInsets;
  className?: string;
  children: ReactNode;
}

/**
 * `math.max(enabled ? padding.side : 0, minimum.side)` — во Flutter минимум
 * применяется и к выключенным сторонам.
 */
function sidePadding(enabled: boolean, side: string, minimum?: string): string {
  if (!enabled) return minimum ?? '0px';
  const inset = `env(safe-area-inset-${side}, 0px)`;
  return minimum === undefined ? inset : `max(${inset}, ${minimum})`;
}

export function SafeArea(props: SafeAreaProps): ReactElement {
  const {
    top = true, bottom = true, left = true, right = true, minimum, className, children,
  } = props;
  const style: CSSProperties = {
    paddingTop: sidePadding(top, 'top', minimum?.top),
    paddingRight: sidePadding(right, 'right', minimum?.right),
    paddingBottom: sidePadding(bottom, 'bottom', minimum?.bottom),
    paddingLeft: sidePadding(left, 'left', minimum?.left),
  };
  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')} style={style}>
      {children}
    </div>
  );
}
